import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Asset, type Voice } from "../../api/client";

export interface UseReferencesResult {
  assets: Asset[];
  voices: Voice[];
  loading: boolean;
  error: string | null;
  registerAsset: () => Promise<void>;
  registerVoice: () => Promise<void>;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/** Lists this tenant's registered assets and voices, refreshable after a
 * new registration (ReferencesPanel calls refresh itself after a successful
 * POST rather than this hook re-fetching on a timer). */
export function useReferences(): UseReferencesResult {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ assets }, { voices }] = await Promise.all([api.listAssets(), api.listVoices()]);
      setAssets(assets);
      setVoices(voices);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { assets, voices, loading, error, registerAsset: refresh, registerVoice: refresh };
}
